/**
 * Выгрузка данных для P&L (ТЗ 0075) — чистые помощники, общие для страницы
 * `/finances/pnl` и кнопки на вкладке «Финансы» карточки группы.
 *
 * Всё, что можно проверить без React и Apollo, живёт здесь: разбор периода,
 * статусов, `stats`, кодов ошибок и base64. Ключи i18n записаны ЦЕЛИКОМ, а не
 * собраны шаблонной строкой: так их видит локаль-гейт
 * (`src/locales/__tests__/pnlExport.locale.test.ts`), и пропущенный перевод
 * ловится тестом, а не глазами на экране.
 */

export type PnlRunStatus = 'pending' | 'running' | 'done' | 'failed';
export type PnlFileFormat = 'xlsx' | 'csv';
export type PnlLanguage = 'ru' | 'pl';
/** Совпадает по значениям с `TagTone` из `_detailShared/detail.styled`. */
export type PnlTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info';

export interface IPnlPeriod {
  monthFrom: string;
  monthTo: string;
}

/** Бэкенд отклоняет период длиннее (`PERIOD_TOO_LONG`). */
export const PNL_MAX_MONTHS = 60;

export const PNL_FILE_FORMATS: PnlFileFormat[] = ['xlsx', 'csv'];

/** Коды ошибок `requestPnlExport` (ТЗ 0075, §6). */
export const PNL_ERROR_CODES = [
  'INVALID_MONTH',
  'INVALID_PERIOD',
  'PERIOD_TOO_LONG',
  'INVALID_FORMAT',
  'GROUP_NOT_FOUND',
  'TOO_MANY_ACTIVE',
] as const;
export type PnlErrorCode = typeof PNL_ERROR_CODES[number];

const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** `YYYY-MM` для даты (по местному времени оператора). */
export const monthKeyOf = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;

export const isValidMonthKey = (value: unknown): value is string =>
  typeof value === 'string' && MONTH_RE.test(value);

/** Порядковый номер месяца: удобно сравнивать и считать длину периода. */
export const monthIndex = (key: string): number => {
  const match = MONTH_RE.exec(key);
  if (!match) return NaN;
  return Number(match[1]) * 12 + Number(match[2]) - 1;
};

export const monthKeyFromIndex = (index: number): string =>
  `${Math.floor(index / 12)}-${pad2((index % 12) + 1)}`;

/** Сколько месяцев в периоде, обе границы включительно. */
export const monthsInPeriod = (monthFrom: string, monthTo: string): number =>
  monthIndex(monthTo) - monthIndex(monthFrom) + 1;

/** Период по умолчанию: январь текущего года → текущий месяц. */
export const defaultPnlPeriod = (today: Date = new Date()): IPnlPeriod => ({
  monthFrom: `${today.getFullYear()}-01`,
  monthTo: monthKeyOf(today),
});

/**
 * Период по умолчанию для группы: месяц старта группы → текущий месяц.
 *
 * - нет даты старта или она не читается — общий период по умолчанию;
 * - группа стартует в будущем — период из одного месяца старта (там уже есть
 *   плановые взносы, а «с сентября по март» бэкенд отклонит как INVALID_PERIOD);
 * - группа старше пяти лет — начало подрезается до 60 месяцев, иначе первый же
 *   клик получит PERIOD_TOO_LONG.
 */
export const groupPnlPeriod = (
  startDate: string | null | undefined,
  today: Date = new Date(),
): IPnlPeriod => {
  const startKey =
    typeof startDate === 'string' ? startDate.slice(0, 7) : undefined;
  if (!isValidMonthKey(startKey)) return defaultPnlPeriod(today);

  const current = monthIndex(monthKeyOf(today));
  const start = monthIndex(startKey);
  if (start >= current) return { monthFrom: startKey, monthTo: startKey };

  const earliest = current - (PNL_MAX_MONTHS - 1);
  return {
    monthFrom: monthKeyFromIndex(Math.max(start, earliest)),
    monthTo: monthKeyFromIndex(current),
  };
};

/**
 * Та же проверка, что делает бэкенд, — чтобы кнопка не отправляла заведомо
 * отклоняемый заказ. Сервер остаётся главным: его коды разбираются так же.
 */
export const validatePnlPeriod = (
  monthFrom: string,
  monthTo: string,
): PnlErrorCode | null => {
  if (!isValidMonthKey(monthFrom) || !isValidMonthKey(monthTo)) {
    return 'INVALID_MONTH';
  }
  const length = monthsInPeriod(monthFrom, monthTo);
  if (length < 1) return 'INVALID_PERIOD';
  if (length > PNL_MAX_MONTHS) return 'PERIOD_TOO_LONG';
  return null;
};

/** Язык подписей колонок в файле: только ru / pl, всё остальное → ru. */
export const pnlLanguage = (language: string | null | undefined): PnlLanguage =>
  typeof language === 'string' && language.toLowerCase().startsWith('pl')
    ? 'pl'
    : 'ru';

/** Заказ ещё собирается — пока такой есть, список надо опрашивать. */
export const isPnlRunActive = (status: string | null | undefined): boolean =>
  status === 'pending' || status === 'running';

export const hasActivePnlRuns = (
  runs: ReadonlyArray<{ status: string }> | null | undefined,
): boolean => (runs ?? []).some((run) => isPnlRunActive(run.status));

const STATUS_LABEL_KEYS: Record<PnlRunStatus, string> = {
  pending: 'pages.pnlExport.status.pending',
  running: 'pages.pnlExport.status.running',
  done: 'pages.pnlExport.status.done',
  failed: 'pages.pnlExport.status.failed',
};

const STATUS_TONES: Record<PnlRunStatus, PnlTone> = {
  pending: 'neutral',
  running: 'info',
  done: 'ok',
  failed: 'danger',
};

const isKnownStatus = (status: unknown): status is PnlRunStatus =>
  typeof status === 'string' &&
  Object.prototype.hasOwnProperty.call(STATUS_LABEL_KEYS, status);

export const pnlStatusLabelKey = (status: string | null | undefined): string =>
  isKnownStatus(status)
    ? STATUS_LABEL_KEYS[status]
    : 'pages.pnlExport.status.unknown';

export const pnlStatusTone = (status: string | null | undefined): PnlTone =>
  isKnownStatus(status) ? STATUS_TONES[status] : 'neutral';

const ERROR_KEYS: Record<PnlErrorCode, string> = {
  INVALID_MONTH: 'pages.pnlExport.errors.INVALID_MONTH',
  INVALID_PERIOD: 'pages.pnlExport.errors.INVALID_PERIOD',
  PERIOD_TOO_LONG: 'pages.pnlExport.errors.PERIOD_TOO_LONG',
  INVALID_FORMAT: 'pages.pnlExport.errors.INVALID_FORMAT',
  GROUP_NOT_FOUND: 'pages.pnlExport.errors.GROUP_NOT_FOUND',
  TOO_MANY_ACTIVE: 'pages.pnlExport.errors.TOO_MANY_ACTIVE',
};

export const PNL_UNKNOWN_ERROR_KEY = 'pages.pnlExport.errors.unknown';
export const PNL_FILE_UNAVAILABLE_KEY =
  'pages.pnlExport.errors.fileUnavailable';

/** Код ошибки сервера → ключ перевода; незнакомый код не остаётся немым. */
export const pnlErrorKey = (code: string | null | undefined): string =>
  typeof code === 'string' &&
  Object.prototype.hasOwnProperty.call(ERROR_KEYS, code)
    ? ERROR_KEYS[code as PnlErrorCode]
    : PNL_UNKNOWN_ERROR_KEY;

/**
 * Поиск группы в селекторе: по названию группы и курса, без учёта регистра и
 * пробелов по краям. Пустой запрос подходит всем.
 */
export const pnlGroupMatches = (
  group: { name: string; courseName?: string | null },
  query: string | null | undefined,
): boolean => {
  const needle = (query ?? '').trim().toLocaleLowerCase();
  if (!needle) return true;
  return [group.name, group.courseName ?? ''].some((text) =>
    text.toLocaleLowerCase().includes(needle),
  );
};

/**
 * `stats` — graphene `JSONString`. На проводе это строка с JSON, но кеш,
 * моки и будущие версии клиента могут отдать уже объект, а сервер, положивший
 * в JSONString готовую строку, — JSON дважды. Разбираем все три случая; в
 * результат попадают только конечные неотрицательные числа.
 */
export const parsePnlStats = (raw: unknown): Record<string, number> => {
  let value: unknown = raw;
  // Не больше трёх разборов подряд: строка, которая разбирается в строку
  // бесконечно, — это мусор, а не выгрузка.
  for (let depth = 0; depth < 3 && typeof value === 'string'; depth += 1) {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  return Object.entries(value as Record<string, unknown>).reduce<
    Record<string, number>
  >((acc, [key, count]) => {
    const numeric = typeof count === 'string' ? Number(count) : count;
    if (
      typeof numeric === 'number' &&
      Number.isFinite(numeric) &&
      numeric >= 0
    ) {
      acc[key] = numeric;
    }
    return acc;
  }, {});
};

/** Период для таблицы: `01.2026 — 09.2026`, один месяц — одной датой. */
export const pnlPeriodLabel = (monthFrom: string, monthTo: string): string => {
  const format = (key: string): string =>
    isValidMonthKey(key) ? `${key.slice(5, 7)}.${key.slice(0, 4)}` : key;
  return monthFrom === monthTo
    ? format(monthFrom)
    : `${format(monthFrom)} — ${format(monthTo)}`;
};

/** Размер файла для подписи под кнопкой «Скачать». */
export const formatPnlFileSize = (bytes: number | null | undefined): string => {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) {
    return '';
  }
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/*
 * base64 → файл: функции переехали в `fileDownload.ts` (ТЗ 0092 — ими же
 * отдаётся лог участия дофинансированных). Реэкспорт оставлен, чтобы старые
 * импорты и мок `core/utils/pnlExport` в тестах P&L работали как раньше.
 */
export { base64ToBytes, saveBytesAsFile } from './fileDownload';
